import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Typography,
  Paper,
  styled,
  alpha,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  SxProps,
  Theme
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { formatCurrency } from '../../utils/formatters';

import { searchInventoryItems } from '../../services/inventoryService';

// دالة البحث الحقيقية التي تجلب المنتجات من قاعدة البيانات
const searchProducts = async (query: string, maxResults?: number) => {
  try {
    const allResults = await searchInventoryItems(query);
    // فلترة المنتجات المخصصة للمتجر فقط
    const storeProducts = allResults.filter(item => item.showInStore !== false);
    // إرجاع عدد محدد من النتائج
    return maxResults ? storeProducts.slice(0, maxResults) : storeProducts;
  } catch (error) {
    console.error('Error in searchProducts:', error);
    return [];
  }
};

// تنسيق مخصص لمربع البحث
const SearchBox = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 0 8px rgba(0,0,0,0.1)',
  },
  width: '100%',
  transition: 'all 0.3s ease',
  border: `1px solid ${theme.palette.divider}`,
}));

// واجهة الخصائص للمكون
interface LiveSearchProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
  minSearchLength?: number;
  maxResults?: number;
  width?: string | number;
  sx?: SxProps<Theme>;
}

const LiveSearch: React.FC<LiveSearchProps> = ({
  onSearch,
  placeholder = 'بحث عن منتجات...',
  minSearchLength = 2,
  maxResults = 10,
  width = '100%',
  sx
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // البحث عن المنتجات عند تغيير مصطلح البحث
  useEffect(() => {
    // إلغاء المؤقت السابق إذا كان موجودًا
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // التحقق من طول مصطلح البحث
    if (searchTerm.length < minSearchLength) {
      setOptions([]);
      return;
    }

    // تعيين مؤقت جديد للبحث (لتجنب البحث مع كل حرف)
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchProducts(searchTerm, maxResults);
        setOptions(results);
      } catch (error) {
        console.error('Error searching products:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    // تنظيف المؤقت عند إلغاء تحميل المكون
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, minSearchLength, maxResults]);

  // معالجة تغيير مصطلح البحث
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setOpen(true);
  };

  const handleProductSelect = (_event: React.SyntheticEvent, value: InventoryItem | string | null) => {
    if (value && typeof value !== 'string') {
      navigate(`/product/${value.id}`);
      setSearchTerm('');
      setOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && searchTerm.trim()) {
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  return (
    <Box
      sx={{
        width,
        maxWidth: '100%',
        ...sx
      }}
    >
      <SearchBox>
        <Autocomplete
          id="live-search"
          open={open && searchTerm.length >= minSearchLength}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={options}
          getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
          loading={loading}
          noOptionsText={loading ? "جاري البحث..." : "لا توجد منتجات مطابقة"}
          onChange={(_, value) => handleProductSelect(_, value)}
          freeSolo
          onKeyDown={handleKeyDown}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main', mr: 1 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                sx: {
                  color: 'text.primary',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& input::placeholder': {
                    color: 'text.secondary',
                    opacity: 0.8
                  },
                  '& .MuiInputAdornment-root': {
                    color: 'primary.main'
                  }
                }
              }}
            />
          )}
          renderOption={(props, option) => (
            <Paper elevation={0} key={option.id} sx={{ width: '100%' }}>
              <ListItem {...props} sx={{ width: '100%' }}>
                <ListItemAvatar>
                  <Avatar
                    src={option.imageUrl || ''}
                    alt={option.name}
                    variant="rounded"
                    sx={{ width: 50, height: 50 }}
                  >
                    {option.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={option.name}
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {option.category}
                      </Typography>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {formatCurrency(option.sellingPrice)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <Divider />
            </Paper>
          )}
          PaperComponent={({ children }) => (
            <Paper
              elevation={6}
              sx={{
                mt: 1,
                borderRadius: 2,
                overflow: 'hidden',
                maxHeight: '70vh'
              }}
            >
              {children}
            </Paper>
          )}
        />
      </SearchBox>
    </Box>
  );
};

export default LiveSearch;
